import { useSelector } from "react-redux";
import styled from "styled-components";

import enhancementsImage from "assets/enhancements.svg";

import BattleRating from "components/base/BattleRating";
import {
  DataWrapper,
  Section,
  SectionContent,
  SectionContentScroll,
  SectionGroup,
  SectionGroups,
  SectionSubtitle,
  SectionTitle,
} from "components/base/DataScreenBase";
import Status from "components/base/Status";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import TooltipMedallions from "components/base/TooltipMedallions";
import TooltipPlanet from "components/base/TooltipPlanet";
import TooltipSector from "components/base/TooltipSector";
import { media } from "components/styles/breakpoints";
import { getPlatinumBattleRatingCount } from "libs/fight";
import {
  getAreaData,
  getPilotData,
  getPlanetData,
  getShipData,
} from "libs/stats";
import {
  getBattleRatings,
  getBuildInfo,
  getLocationInfo,
  getMedallions,
} from "redux/selectors/characterSelectors";
import { COLORS } from "utils/constants";

interface IconProps {
  image: string;
}

const InfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const VitalsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${media.desktop`
    gap: 10px;
  `}
`;

const VitalIcon = styled.div<IconProps>`
  height: 16px;
  width: 16px;
  mask: url(${(props) => props.image}) no-repeat center;
`;

const VitalWrapper = styled.div<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${(props) => props.color};

  ${media.desktop`
    font-size: 18px;
  `}

  ${VitalIcon} {
    background-color: ${(props) => props.color};
  }
`;

const VitalValue = styled.div``;

const SectorIcon = styled.div`
  font-size: 22px;
  line-height: 16px;
`;

export default function Vitals() {
  const { ship, pilot } = useSelector(getBuildInfo);
  const { planet, area } = useSelector(getLocationInfo);
  const medallions = useSelector(getMedallions);
  const battleRatings = useSelector(getBattleRatings);

  const {
    name: shipClass,
    description: shipDesc,
    color: shipColor,
  } = getShipData(ship);
  const { name: pilotProfession, description: pilotDesc } = getPilotData(pilot);
  const { name: planetName, color: planetColor } = getPlanetData(planet);
  const { name: sectorName } = getAreaData(area);

  const platinumRatings = getPlatinumBattleRatingCount(battleRatings);

  return (
    <DataWrapper>
      <Section>
        <SectionTitle>Status</SectionTitle>
        <SectionContent>
          <Status />
        </SectionContent>
      </Section>
      <Section>
        <SectionTitle>Core Data</SectionTitle>
        <SectionContentScroll>
          <SectionGroups>
            <SectionGroup>
              <SectionSubtitle>Build</SectionSubtitle>
              <VitalsGroup>
                <Tooltip
                  color={shipColor}
                  content={
                    <>
                      <TooltipName>Starship: {shipClass}</TooltipName>
                      <TooltipDesc>{shipDesc}</TooltipDesc>
                    </>
                  }
                >
                  <InfoWrapper>
                    <Name>Starship Class</Name>
                    <VitalWrapper color={shipColor}>
                      <VitalValue>{shipClass}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
                <Tooltip
                  color={COLORS.PILOTS}
                  content={
                    <>
                      <TooltipName>Pilot: {pilotProfession}</TooltipName>
                      <TooltipDesc>{pilotDesc}</TooltipDesc>
                    </>
                  }
                >
                  <InfoWrapper>
                    <Name>Pilot Discipline</Name>
                    <VitalWrapper color={COLORS.PILOTS}>
                      <VitalValue>{pilotProfession}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
              </VitalsGroup>
            </SectionGroup>
            <SectionGroup>
              <SectionSubtitle>Standing</SectionSubtitle>
              <VitalsGroup>
                <Tooltip
                  color={COLORS.ENHANCEMENTS}
                  content={<TooltipMedallions />}
                >
                  <InfoWrapper>
                    <Name>Earned Medallions</Name>
                    <VitalWrapper color={COLORS.ENHANCEMENTS}>
                      <VitalIcon image={enhancementsImage} />
                      <VitalValue>{medallions.total}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
                <Tooltip
                  color={COLORS.RATINGS.PLATINUM}
                  content={
                    <>
                      <TooltipName>Platinum Battle Ratings</TooltipName>
                      <TooltipDesc>{`You have earned ${platinumRatings} platinum battle ratings, by scoring dominant victories against opponents.`}</TooltipDesc>
                    </>
                  }
                >
                  <InfoWrapper>
                    <Name>Platinum Battle Ratings</Name>
                    <VitalWrapper color={COLORS.RATINGS.PLATINUM}>
                      <BattleRating rating="platinum" iconOnly noTooltip />
                      <VitalValue>{platinumRatings}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
              </VitalsGroup>
            </SectionGroup>
            <SectionGroup>
              <SectionSubtitle>Spend</SectionSubtitle>
              <VitalsGroup>
                <Tooltip
                  color={COLORS.ENHANCEMENTS}
                  content={<TooltipMedallions />}
                >
                  <InfoWrapper>
                    <Name>Available Medallions</Name>
                    <VitalWrapper color={COLORS.ENHANCEMENTS}>
                      <VitalIcon image={enhancementsImage} />
                      <VitalValue>
                        {medallions.available} / {medallions.total}
                      </VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
              </VitalsGroup>
            </SectionGroup>
            <SectionGroup>
              <SectionSubtitle>Location</SectionSubtitle>
              <VitalsGroup>
                <Tooltip content={<TooltipPlanet />} color={planetColor}>
                  <InfoWrapper>
                    <Name>Current Planet</Name>
                    <VitalWrapper color={planetColor}>
                      <VitalValue>❍ {planetName}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
                <Tooltip content={<TooltipSector />} color={COLORS.LOCATION}>
                  <InfoWrapper>
                    <Name>Sector</Name>
                    <VitalWrapper color={COLORS.LOCATION}>
                      <SectorIcon>⌔</SectorIcon>
                      <VitalValue>{sectorName}</VitalValue>
                    </VitalWrapper>
                  </InfoWrapper>
                </Tooltip>
              </VitalsGroup>
            </SectionGroup>
          </SectionGroups>
        </SectionContentScroll>
      </Section>
    </DataWrapper>
  );
}
